import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, CheckCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ModernCaptchaProps {
  onVerify: (token: string, honeypotData: HoneypotData) => void;
  language?: 'es' | 'en';
  disabled?: boolean;
}

interface HoneypotData {
  honeypot1: string;
  honeypot2: string;
  startTime: number;
  interactionCount: number;
  mouseMovements: number;
}

interface ChallengeData {
  type: 'math' | 'sequence' | 'logic' | 'pattern';
  question: string;
  answer: string;
  challengeToken: string;
}

export const ModernCaptcha: React.FC<ModernCaptchaProps> = ({ 
  onVerify, 
  language = 'es', 
  disabled = false 
}) => {
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [challenge, setChallenge] = useState<ChallengeData | null>(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [honeypotData, setHoneypotData] = useState<HoneypotData>({
    honeypot1: '',
    honeypot2: '',
    startTime: Date.now(),
    interactionCount: 0,
    mouseMovements: 0
  });
  
  const containerRef = useRef<HTMLDivElement>(null);

  const content = {
    es: {
      title: "Verificación de Seguridad",
      subtitle: "Completa esta verificación para continuar",
      challenge: "Resuelve este desafío:",
      placeholder: "Tu respuesta...",
      verify: "Verificar",
      verifying: "Verificando...",
      verified: "✓ Verificado",
      newChallenge: "Nuevo desafío",
      attempts: "Intentos restantes:",
      failed: "Verificación fallida. Inténtalo de nuevo.",
      loadError: "No se pudo cargar la verificación de seguridad.",
      retry: "Reintentar",
      loading: "Cargando verificación..."
    },
    en: {
      title: "Security Verification",
      subtitle: "Complete this verification to continue",
      challenge: "Solve this challenge:",
      placeholder: "Your answer...",
      verify: "Verify",
      verifying: "Verifying...",
      verified: "✓ Verified",
      newChallenge: "New challenge",
      attempts: "Attempts remaining:",
      failed: "Verification failed. Please try again.",
      loadError: "The security verification could not be loaded.",
      retry: "Retry",
      loading: "Loading verification..."
    }
  };

  const t = content[language];

  // Challenges are generated and validated server-side (signed tokens)
  const loadChallenge = async () => {
    setIsLoading(true);
    setLoadError(false);
    const { data, error } = await supabase.functions.invoke('verify-captcha', {
      body: { action: 'challenge', language }
    });
    setIsLoading(false);
    if (error || !data?.success) {
      setChallenge(null);
      setLoadError(true);
      return;
    }
    setChallenge({ type: 'math', question: data.question, answer: '', challengeToken: data.challengeToken });
  };

  useEffect(() => {
    loadChallenge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Track mouse movements for behavioral analysis
  useEffect(() => {
    const handleMouseMove = () => {
      setHoneypotData(prev => ({
        ...prev,
        mouseMovements: prev.mouseMovements + 1,
        interactionCount: prev.interactionCount + 1
      }));
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      return () => container.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  const handleVerification = async () => {
    if (!challenge || !userAnswer.trim()) return;

    setIsVerifying(true);

    // Honeypots remain hard-fail signals. Mouse/interaction counts are telemetry
    // only, so touch, keyboard-only and assistive-tech users are never blocked.
    const isSuspicious =
      honeypotData.honeypot1 !== '' || honeypotData.honeypot2 !== '';

    let token: string | null = null;
    if (!isSuspicious) {
      const { data, error } = await supabase.functions.invoke('verify-captcha', {
        body: {
          action: 'verify',
          challengeToken: challenge.challengeToken,
          answer: userAnswer.trim()
        }
      });
      if (!error && data?.success && data?.token) {
        token = data.token as string;
      }
    }


    if (token) {
      setIsVerified(true);
      onVerify(token, honeypotData);
    } else {
      setAttempts(prev => prev + 1);
      if (attempts >= 2) {
        await loadChallenge();
        setAttempts(0);
      }
      setUserAnswer('');
    }

    setIsVerifying(false);
  };

  const handleNewChallenge = () => {
    loadChallenge();
    setUserAnswer('');
    setAttempts(0);
    setHoneypotData(prev => ({
      ...prev,
      startTime: Date.now(),
      interactionCount: 0,
      mouseMovements: 0
    }));
  };

  if (isVerified) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-green-800 font-medium">{t.verified}</span>
            <Badge variant="secondary" className="ml-auto">
              <Shield className="h-3 w-3 mr-1" />
              Seguro
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-blue-200" ref={containerRef}>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="h-5 w-5 text-blue-600" />
          <div>
            <h3 className="font-semibold text-sm">{t.title}</h3>
            <p className="text-xs text-muted-foreground">{t.subtitle}</p>
          </div>
        </div>

        {/* Honeypot fields - hidden from users but visible to bots */}
        <div style={{ display: 'none' }}>
          <input
            type="text"
            name="website_url"
            tabIndex={-1}
            autoComplete="off"
            value={honeypotData.honeypot1}
            onChange={(e) => setHoneypotData(prev => ({ ...prev, honeypot1: e.target.value }))}
            aria-hidden="true"
          />
          <input
            type="email"
            name="email_confirmation"
            tabIndex={-1}
            autoComplete="off"
            value={honeypotData.honeypot2}
            onChange={(e) => setHoneypotData(prev => ({ ...prev, honeypot2: e.target.value }))}
            aria-hidden="true"
          />
        </div>

        {isLoading && !challenge && !loadError && (
          <p className="text-sm text-muted-foreground" role="status">{t.loading}</p>
        )}

        {loadError && (
          <div className="space-y-3" role="alert">
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">{t.loadError}</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={loadChallenge}
              disabled={disabled || isLoading}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              {t.retry}
            </Button>
          </div>
        )}

        {challenge && (
          <div className="space-y-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-900">{t.challenge}</p>
              <p className="text-lg font-bold text-blue-700 mt-1">{challenge.question}</p>
            </div>

            <div className="flex min-w-0 gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={(e) => {
                  setUserAnswer(e.target.value);
                  setHoneypotData(prev => ({ 
                    ...prev, 
                    interactionCount: prev.interactionCount + 1 
                  }));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleVerification();
                  }
                }}
                aria-label={t.challenge}
                placeholder={t.placeholder}
                className="min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"

                disabled={disabled || isVerifying}
              />
              <Button
                type="button"
                onClick={handleVerification}
                disabled={disabled || isVerifying || !userAnswer.trim()}
                size="sm"
                className="shrink-0 px-4"
              >
                {isVerifying ? t.verifying : t.verify}
              </Button>
            </div>

            <div className="flex justify-between items-center text-xs text-muted-foreground">
              <span>{t.attempts} {3 - attempts}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleNewChallenge}
                className="h-auto p-1 text-xs"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                {t.newChallenge}
              </Button>
            </div>

            {attempts > 0 && (
              <div className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm">{t.failed}</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};